import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Bot,
  CheckCircle2,
  CreditCard,
  Gift,
  QrCode,
  Wallet,
} from "lucide-react";

const steps = [
  {
    number: "1",
    title: "Purchase WhatsApp Automation Software (₹1,000)",
    description:
      "Register an account and activate the software license. Once payment is verified, your software dashboard, task center, and referral ID are immediately unlocked.",
    icon: CreditCard,
  },
  {
    number: "2",
    title: "Execute Real Campaign Tasks",
    description:
      "Work on live assigned campaigns: configure keywords, dispatch approved broadcasts, verify templates, and test automated chat sequences. Submit transaction IDs or verification proofs for administrative review.",
    icon: Bot,
  },
  {
    number: "3",
    title: "Refer & Earn ₹200 Per Verified Purchase",
    description:
      "Invite team members, marketers, and business owners who need WhatsApp automation. When your referee pays ₹1,000, our system validates the receipt and credits ₹200 to your wallet. You can view your direct team and monitor their software status.",
    icon: Gift,
  },
  {
    number: "4",
    title: "Withdraw to UPI or QR Code",
    description:
      "Add your UPI ID or payment QR code. Request withdrawals directly from your approved wallet balance once you reach the minimum threshold of ₹500. Admin verifies bank payouts with genuine UTR numbers.",
    icon: QrCode,
  },
];

function HowItWorks() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoaded(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  return (
    <main className="min-h-screen overflow-hidden bg-[#f6f8fb] text-slate-900">
      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden bg-[#08111f] text-white">
        {/* Background animation */}
        <div className="pointer-events-none absolute -left-40 top-10 h-96 w-96 animate-[floatGlow_7s_ease-in-out_infinite] rounded-full bg-emerald-500/10 blur-[120px]" />

        <div className="pointer-events-none absolute -right-40 bottom-0 h-[28rem] w-[28rem] animate-[floatGlow_9s_ease-in-out_infinite_reverse] rounded-full bg-blue-500/10 blur-[130px]" />

        <div className="pointer-events-none absolute left-1/2 top-0 h-px w-1/2 -translate-x-1/2 bg-gradient-to-r from-transparent via-emerald-400/50 to-transparent" />

        <div className="relative mx-auto max-w-7xl px-5 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24">
          <div
            className={`mx-auto max-w-3xl text-center transition-all duration-1000 ${
              loaded
                ? "translate-y-0 opacity-100"
                : "translate-y-8 opacity-0"
            }`}
          >
            {/* Refrex Brand */}
            <div className="mb-7 inline-flex items-center gap-2.5 rounded-full border border-emerald-400/20 bg-emerald-400/[0.08] px-4 py-2">
              <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-[#00a96d] shadow-lg shadow-emerald-900/30">
                <span className="text-xs font-black text-white">R</span>
              </div>

              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-300">
                Referx
              </span>
            </div>

            <p className="text-[10px] font-black uppercase tracking-[0.22em] text-emerald-400">
              Step-by-Step Overview
            </p>

            <h1 className="mt-4 text-4xl font-black leading-[1.05] tracking-[-0.055em] sm:text-5xl lg:text-6xl">
              How <span className="text-emerald-400">Referx</span> Works
            </h1>

            <p className="mx-auto mt-6 max-w-2xl text-sm leading-7 text-slate-400 sm:text-base">
              Our platform merges cutting-edge WhatsApp automation software
              with a task-based productivity model and a verified referral
              partner program.
            </p>
          </div>
        </div>
      </section>

      {/* ================= STEPS ================= */}
      <section className="relative">
        {/* subtle background */}
        <div className="pointer-events-none absolute left-1/2 top-20 h-96 w-96 -translate-x-1/2 rounded-full bg-emerald-100/30 blur-[130px]" />

        <div className="relative mx-auto max-w-6xl px-5 py-14 sm:px-6 sm:py-20 lg:px-8 lg:py-24">
          <div className="space-y-6 lg:space-y-7">
            {steps.map((step, index) => {
              const Icon = step.icon;

              return (
                <div
                  key={step.number}
                  className={`group relative transition-all duration-700 ${
                    loaded
                      ? "translate-y-0 opacity-100"
                      : "translate-y-12 opacity-0"
                  }`}
                  style={{
                    transitionDelay: `${index * 150 + 200}ms`,
                  }}
                >
                  {/* Connector */}
                  {index < steps.length - 1 && (
                    <div className="absolute left-[31px] top-[78px] hidden h-[calc(100%+28px)] w-px bg-gradient-to-b from-emerald-300 via-emerald-200 to-transparent lg:block" />
                  )}

                  <div className="relative overflow-hidden rounded-[26px] border border-slate-200/90 bg-white shadow-[0_10px_40px_rgba(15,23,42,0.055)] transition-all duration-500 hover:-translate-y-1 hover:border-emerald-200 hover:shadow-[0_22px_60px_rgba(15,23,42,0.10)]">
                    {/* animated green edge */}
                    <div className="absolute bottom-0 left-0 top-0 w-1 origin-bottom scale-y-0 bg-[#00a96d] transition-transform duration-500 group-hover:scale-y-100" />

                    <div className="flex flex-col gap-5 p-6 sm:p-7 md:flex-row md:items-start md:gap-7 lg:p-8">
                      {/* NUMBER */}
                      <div className="relative shrink-0">
                        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50 text-lg font-black text-emerald-600 ring-1 ring-emerald-100 transition-all duration-500 group-hover:scale-105 group-hover:bg-[#00a96d] group-hover:text-white group-hover:shadow-lg group-hover:shadow-emerald-200">
                          {step.number}
                        </div>

                        {/* icon */}
                        <div className="absolute -bottom-2 -right-2 flex h-7 w-7 items-center justify-center rounded-lg border border-white bg-slate-900 text-emerald-400 shadow-md transition-all duration-500 group-hover:rotate-6">
                          <Icon size={13} strokeWidth={2} />
                        </div>
                      </div>

                      {/* CONTENT */}
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div>
                            <div className="mb-2 text-[9px] font-black uppercase tracking-[0.18em] text-emerald-600">
                              Step {step.number}
                            </div>

                            <h2 className="text-lg font-black leading-snug tracking-[-0.025em] text-slate-900 sm:text-xl lg:text-[22px]">
                              {step.title}
                            </h2>
                          </div>

                          {/* small status */}
                          <div className="flex shrink-0 items-center gap-1.5 self-start rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1.5">
                            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />

                            <span className="text-[9px] font-bold text-emerald-700">
                              {index === 0
                                ? "Get Started"
                                : index === 1
                                ? "Work"
                                : index === 2
                                ? "Earn"
                                : "Withdraw"}
                            </span>
                          </div>
                        </div>

                        <p className="mt-4 max-w-4xl text-[13px] leading-6 text-slate-500 sm:text-sm sm:leading-7">
                          {step.description}
                        </p>

                        <div className="mt-5 flex items-center gap-2 text-[10px] font-semibold text-slate-400">
                          <CheckCircle2
                            size={14}
                            className="text-emerald-500"
                          />

                          <span>
                            {index === 0 &&
                              "Dashboard, task center & referral ID unlocked"}
                            {index === 1 &&
                              "Assigned campaigns and verification proofs"}
                            {index === 2 &&
                              "₹200 credited for every verified purchase"}
                            {index === 3 &&
                              "UPI or QR withdrawal from approved wallet"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= FINAL CTA ================= */}
      <section className="relative overflow-hidden bg-[#08111f]">
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-80 w-80 -translate-x-1/2 -translate-y-1/2 animate-pulse rounded-full bg-emerald-500/10 blur-[110px]" />

        <div
          className={`relative mx-auto max-w-4xl px-5 py-14 text-center transition-all delay-700 duration-1000 sm:px-6 sm:py-16 lg:px-8 ${
            loaded
              ? "translate-y-0 opacity-100"
              : "translate-y-8 opacity-0"
          }`}
        >
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl border border-emerald-400/20 bg-emerald-400/10 text-emerald-400">
            <Wallet size={21} />
          </div>

          <h2 className="mt-5 text-2xl font-black tracking-[-0.04em] text-white sm:text-3xl">
            Start Today
          </h2>

          <Link
            to="/signup"
            className="group mt-6 inline-flex min-h-12 items-center justify-center rounded-xl bg-[#00a96d] px-6 py-3 text-sm font-black text-white shadow-xl shadow-emerald-950/30 transition-all duration-300 hover:-translate-y-1 hover:bg-[#00945f] hover:shadow-2xl hover:shadow-emerald-950/40"
          >
            Purchase Software (₹1,000)

            <ArrowRight
              size={16}
              className="ml-2 transition-transform duration-300 group-hover:translate-x-1"
            />
          </Link>
        </div>
      </section>

      {/* Local animation */}
      <style>{`
        @keyframes floatGlow {
          0%,
          100% {
            transform: translate3d(0, 0, 0) scale(1);
          }

          50% {
            transform: translate3d(0, -20px, 0) scale(1.05);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          *,
          *::before,
          *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>
    </main>
  );
}

export default HowItWorks;